import React, { useState, useEffect, useCallback, useMemo } from "react";
import Accordion from "@mui/material/Accordion";
import AccordionSummary from "@mui/material/AccordionSummary";
import AccordionDetails from "@mui/material/AccordionDetails";
import Typography from "@mui/material/Typography";
import ExpandMoreIcon from "@mui/icons-material/ExpandMore";
import Swal from "sweetalert2";
import DataTable from "../component/DataTable";
import axiosInstance from "../axiosInterceptor";
import { toast } from "react-toastify";
import LightboxWrapper from "../component/LightboxWrapper";
import { useNavigate } from "react-router-dom";
import { InputAdornment, TextField } from "@mui/material";
import SearchIcon from "@mui/icons-material/Search";

// import { GridActionsCellItem } from "@mui/x-data-grid";
// import { Tooltip } from "@mui/material";
// import VisibilityIcon from "@mui/icons-material/Visibility";

const Product = () => {
  const navigate = useNavigate();

  const [rowsData, setRowsData] = useState([]);
  const [field, setField] = useState("");
  const [headerName, setHeaderName] = useState("");
  const [loading, setLoading] = useState(false);

  const [search, setSearch] = useState("");
  const [debouncedSearch, setDebouncedSearch] = useState("");

  useEffect(() => {
    const timer = setTimeout(() => {
      setDebouncedSearch(search);
    }, 500);

    return () => clearTimeout(timer);
  }, [search]);

  const handleViewDetails = useCallback(
    (product) => {
      const productId = product?._id;
      if (!productId) return;

      navigate(`/products/${productId}`, {
        state: { productId },
      });
    },
    [navigate]
  );

  const baseColumns = useMemo(
    () => [
      {
        field: "image",
        headerName: "Product Image",
        flex: 0.7,
        renderCell: (params) => {
          const thumbImage = params.value;
          if (!thumbImage) {
            return <span style={{ fontSize: "18px" }}>–</span>;
          }

          return (
            <LightboxWrapper>
              <img
                src={thumbImage}
                alt="Product Thumbnail"
                loading="lazy"
                style={{
                  // width: 100,
                  height: 100,
                  objectFit: "cover",
                  display: "block",
                  backgroundColor: "#000",
                }}
                onError={(e) => {
                  e.target.onerror = null;
                  e.target.style.display = "none";
                  e.target.parentNode.innerHTML =
                    "<span style='font-size:18px'>–</span>";
                }}
              />
            </LightboxWrapper>
          );
        },
      },
      {
        field: "sku",
        headerName: "SKU",
        flex: 0.8,
        renderCell: (params) => {
          const sku = params.row?.variants[0]?.sku || "-";
          return sku
        }
      },
      {
        field: "id",
        headerName: "#ID",
        flex: 0.8,
      },
      {
        field: "title",
        headerName: "Title",
        flex: 1.5,
      },
      {
        field: "product_type",
        headerName: "Product Type",
        flex: 0.8,
      },
      // {
      //   field: "created_at",
      //   headerName: "Created Date",
      //   flex: 1,
      //   renderCell: (params) => {
      //     const value = params?.row?.created_at ?? params?.row?.date ?? params?.value;
      //     const date = new Date(value);
      //     return new Intl.DateTimeFormat("en-GB", {
      //       day: "2-digit",
      //       month: "short",
      //       year: "numeric",
      //       hour: "2-digit",
      //       minute: "2-digit",
      //       hour12: true,
      //     }).format(date).replace(/am|pm/i, (m) => m.toUpperCase());
      //   },
      // },
      {
        field: "updated_at",
        headerName: "Updated Date",
        flex: 1,
        renderCell: (params) => {
          const value = params?.row?.updated_at ?? params?.row?.date ?? params?.value;
          const date = new Date(value);
          return new Intl.DateTimeFormat("en-GB", {
            day: "2-digit",
            month: "short",
            year: "numeric",
            hour: "2-digit",
            minute: "2-digit",
            hour12: true,
          }).format(date).replace(/am|pm/i, (m) => m.toUpperCase());
        },
      },
      {
        field: "actions",
        headerName: "Actions",
        flex: 0.5,
        sortable: false,
        filterable: false,
        // renderCell: (params) => (
        //   <>  <GridActionsCellItem
        //     icon={
        //       <Tooltip title="View Product Details">
        //         <VisibilityIcon color="primary" />
        //       </Tooltip>
        //     }
        //     label="View"
        //     showInMenu={false}
        //     onClick={() => handleViewDetails(params.row)}
        //   /></>
        // )
        renderCell: (params) => (
          <button
            type="button"
            className="btn btn-sm btn-primary"
            onClick={() => handleViewDetails(params.row)}
          >
            View
          </button>
        ),
      },
    ],
    [handleViewDetails]
  );

  const [columns, setColumns] = useState(baseColumns);
  const [totalRecords, setTotalRecords] = useState(0);
  const [paginationModel, setPaginationModel] = useState({
    page: 0,
    pageSize: 10,
  });

  // const fetchData = useCallback(async () => {
  //   setLoading(true);
  //   try {
  //     const { data } = await axiosInstance.get(`shopify/getAllProducts?page=${paginationModel.page + 1}&limit=${paginationModel.pageSize}`);

  //     if (data?.data) {
  //       setRowsData(data.data);
  //       setTotalRecords(data.totalRecords);
  //     }
  //   } catch (error) {
  //     const errMsg =
  //       error?.response?.data?.message ||
  //       error?.message ||
  //       "Something went wrong!";
  //     toast.error(errMsg);
  //   } finally {
  //     setLoading(false);
  //   }
  // }, [paginationModel.page, paginationModel.pageSize]);

  const fetchData = useCallback(async () => {
    setLoading(true);
    try {
      let url = `shopify/getAllProducts?page=${paginationModel.page + 1}&limit=${paginationModel.pageSize}`;

      // if search OR country filter exists → use search API
      if (debouncedSearch) {

        url = `shopify/getProductBySearch?page=${paginationModel.page + 1}&limit=${paginationModel.pageSize}`;

        if (debouncedSearch) {
          url += `&search=${debouncedSearch}`;
        }
      }

      const { data } = await axiosInstance.get(url);

      if (data?.data) {
        setRowsData(data.data);
        setTotalRecords(debouncedSearch ? data.total : data.totalRecords);
      }
    } catch (error) {
      const errMsg =
        error?.response?.data?.message ||
        error?.message ||
        "Something went wrong!";
      toast.error(errMsg);
    } finally {
      setLoading(false);
    }
  }, [paginationModel.page, paginationModel.pageSize, debouncedSearch]);

  useEffect(() => {
    fetchData();
  }, [fetchData]);

  const keys = rowsData.length > 0 ? Object.keys(rowsData[0]) : [];

  const handleAddColumn = (e) => {
    e.preventDefault();

    if (!field || !headerName) return;

    const isDuplicate = columns.some((col) => col.field === field);
    if (isDuplicate) {
      Swal.fire("Field already exists!", "", "warning");
      return;
    }

    const newColumn = {
      field: field.trim(),
      headerName: headerName.trim(),
      flex: 1,
    };

    const actionCol = columns.find((col) => col.field === "actions");
    const otherCols = columns.filter((col) => col.field !== "actions");

    setColumns([...otherCols, newColumn, actionCol]);
    setField("");
    setHeaderName("");
  };

  return (
    <div className="content-wrapper pd-top">
      <div className="page-header mt-5">
        <h3 className="page-title">Products</h3>
      </div>

      {/* Column Add Form */}
      <div className="mb-4">
        <Accordion>
          <AccordionSummary
            expandIcon={<ExpandMoreIcon />}
            aria-controls="panel1-content"
            id="panel1-header"
          >
            <Typography component="span">
              <h5 className="m-0">Add Dynamic Column</h5>
            </Typography>
          </AccordionSummary>
          <AccordionDetails>
            <form
              onSubmit={handleAddColumn}
              className="p-3 border rounded bg-white"
            >
              <div className="row">
                <div className="col-md-6">
                  <div className="form-group mb-3">
                    <label>Field</label>
                    <select
                      className="form-control"
                      value={field}
                      onChange={(e) => setField(e.target.value)}
                    >
                      <option value="" disabled>
                        Select a field
                      </option>
                      {keys.map((key, idx) => (
                        <option key={idx} value={key}>
                          {key}
                        </option>
                      ))}
                    </select>
                  </div>
                </div>
                <div className="col-md-6">
                  <div className="form-group mb-3">
                    <label>Header Name</label>
                    <input
                      type="text"
                      className="form-control"
                      value={headerName}
                      onChange={(e) => setHeaderName(e.target.value)}
                      placeholder="e.g. Email"
                    />
                  </div>
                </div>
              </div>
              <div style={{ textAlign: "right" }}>
                <button type="submit" className="btn btn-primary">
                  Add Column
                </button>
              </div>
            </form>
          </AccordionDetails>
        </Accordion>
      </div>

      {/* Search Bar */}
      <div className="mb-4 d-flex"
      >
        <div style={{ flex: 1 }}>

          <TextField
            fullWidth
            style={{ width: "100%" }}
            size="small"
            placeholder="Find products by title, tag, vendor, type or ID"
            value={search}
            onChange={(e) => setSearch(e.target.value)}
            sx={{
              backgroundColor: "#fff",
              borderRadius: "8px",
              "& .MuiOutlinedInput-root": {
                borderRadius: "8px",
                "& fieldset": {
                  borderColor: "#d0d5dd",
                  borderWidth: "1px",
                },
                "&:hover fieldset": {
                  borderColor: "#1976d2"
                },
                "&.Mui-focused fieldset": {
                  borderColor: "#1976d2",
                  borderWidth: "1px",
                }
              }
            }}
            InputProps={{
              startAdornment: (
                <InputAdornment position="start">
                  <SearchIcon sx={{ color: "#1976d2" }} />
                </InputAdornment>
              )
            }}
          />
        </div>
      </div>

      {/* Data Table */}
      <div className="row">
        <div className="col-lg-12">
          <DataTable
            key={paginationModel.page}
            rows={rowsData}
            columns={columns}
            getRowId={(row) => row?._id}
            paginationModel={paginationModel}
            onPaginationModelChange={setPaginationModel}
            rowCount={totalRecords}
            paginationMode="server"
            loading={loading}
            disableVirtualization
            getRowHeight={() => 100}
          />
        </div>
      </div>
    </div>
  );
};

export default Product;
